// Course management functions
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, getDoc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from './config';

/**
 * Get all courses
 */
export const getAllCourses = async (filters = {}) => {
  try {
    const coursesRef = collection(db, 'courses');
    let q = query(coursesRef, orderBy('createdAt', 'desc'));
    
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting courses:', error);
    return [];
  }
};

/**
 * Get a single course by ID
 */
export const getCourse = async (courseId) => {
  try {
    const courseRef = doc(db, 'courses', courseId);
    const snapshot = await getDoc(courseRef);
    
    if (snapshot.exists()) {
      return { id: snapshot.id, ...snapshot.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting course:', error);
    return null;
  }
};

/**
 * Create a new course
 */
export const createCourse = async (courseData) => {
  try {
    const coursesRef = collection(db, 'courses');
    
    const newCourse = {
      ...courseData,
      status: courseData.status || 'draft',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(coursesRef, newCourse);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating course:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a course
 */
export const updateCourse = async (courseId, updates) => {
  try {
    const courseRef = doc(db, 'courses', courseId);
    await updateDoc(courseRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating course:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a course
 */
export const deleteCourse = async (courseId) => {
  try {
    const courseRef = doc(db, 'courses', courseId);
    await deleteDoc(courseRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting course:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get courses for a specific student (enrolled courses)
 */
export const getStudentCourses = async (studentEmail) => {
  try {
    const enrollmentsRef = collection(db, 'courseEnrollments');
    const q = query(
      enrollmentsRef,
      where('studentEmail', '==', studentEmail.toLowerCase().trim()),
      where('status', '==', 'active')
    );
    
    const snapshot = await getDocs(q);
    const enrollmentIds = snapshot.docs.map(doc => doc.data().courseId);
    
    if (enrollmentIds.length === 0) {
      return [];
    }
    
    // Get course details
    const courses = await Promise.all(
      enrollmentIds.map(courseId => getCourse(courseId))
    );
    
    return courses.filter(course => course !== null);
  } catch (error) {
    console.error('Error getting student courses:', error);
    return [];
  }
};

/**
 * Enroll student in course
 */
export const enrollStudentInCourse = async (courseId, studentEmail, studentName) => {
  try {
    // Check if already enrolled
    const enrollmentsRef = collection(db, 'courseEnrollments');
    const q = query(
      enrollmentsRef,
      where('courseId', '==', courseId),
      where('studentEmail', '==', studentEmail.toLowerCase().trim())
    );
    
    const existing = await getDocs(q);
    if (!existing.empty) {
      return { success: true, id: existing.docs[0].id, alreadyEnrolled: true };
    }
    
    // Create enrollment
    const docRef = await addDoc(enrollmentsRef, {
      courseId,
      studentEmail: studentEmail.toLowerCase().trim(),
      studentName: studentName || '',
      status: 'active',
      enrolledAt: serverTimestamp(),
      progress: {
        completedLessons: [],
        lastAccessedAt: serverTimestamp()
      }
    });
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error enrolling student:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update course progress
 */
export const updateCourseProgress = async (courseId, studentEmail, lessonId) => {
  try {
    const enrollmentsRef = collection(db, 'courseEnrollments');
    const q = query(
      enrollmentsRef,
      where('courseId', '==', courseId),
      where('studentEmail', '==', studentEmail.toLowerCase().trim())
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      throw new Error('Enrollment not found');
    }
    
    const enrollmentRef = doc(db, 'courseEnrollments', snapshot.docs[0].id);
    const enrollmentData = snapshot.docs[0].data();
    
    const completedLessons = enrollmentData.progress?.completedLessons || [];
    if (!completedLessons.includes(lessonId)) {
      completedLessons.push(lessonId);
    }
    
    await updateDoc(enrollmentRef, {
      'progress.completedLessons': completedLessons,
      'progress.lastAccessedAt': serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating course progress:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get course enrollment for a student
 */
export const getCourseEnrollment = async (courseId, studentEmail) => {
  try {
    const enrollmentsRef = collection(db, 'courseEnrollments');
    const q = query(
      enrollmentsRef,
      where('courseId', '==', courseId),
      where('studentEmail', '==', studentEmail.toLowerCase().trim())
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }
    
    return {
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data()
    };
  } catch (error) {
    console.error('Error getting course enrollment:', error);
    return null;
  }
};
